import { Avatar, Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { useAdminMeQuery } from '../../api';

interface Props {
  /** Modo compacto: só o avatar pequeno, sem nome/empresa. */
  dense?: boolean;
}

/**
 * Corte em duas linhas com reticências. O drawer tem 260px e o bloco de perfil
 * fica preso em 200px; sem isso um nome longo empurrava o menu inteiro pra
 * baixo. Duas linhas cobrem a maioria das razões sociais — o resto vira "…" e
 * o texto completo fica no tooltip.
 */
const duasLinhasSx = {
  maxWidth: 200,
  overflowWrap: 'anywhere' as const,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

/**
 * Bloco de perfil no drawer admin. Mostra avatar (DiceBear bottts) + nome do
 * usuário + nome da unidade. Inspirado em `Profile.js` do sischef-app.
 */
export function ProfileAdmin({ dense = false }: Props): JSX.Element {
  const { data: me, isLoading } = useAdminMeQuery();

  if (isLoading || !me) {
    return (
      <Box sx={containerSx(dense)}>
        <Skeleton variant="circular" width={dense ? 32 : 56} height={dense ? 32 : 56} />
        {!dense && (
          <>
            <Skeleton variant="text" width={140} sx={{ mt: 1 }} />
            <Skeleton variant="text" width={100} />
          </>
        )}
      </Box>
    );
  }

  const nomeCompleto =
    [me.nome, me.sobrenome].filter(Boolean).join(' ').trim() || me.username || 'Usuário';
  const seed = me.email ?? me.username ?? 'admin';
  const empresa = me.nomeUnidade ?? (me.idUnidade != null ? `Unidade #${me.idUnidade}` : '--');
  const avatarUrl = `https://api.dicebear.com/7.x/bottts/svg?seed=${encodeURIComponent(seed)}`;

  return (
    <Box sx={containerSx(dense)}>
      <Tooltip title={dense ? nomeCompleto : ''}>
        <Avatar
          alt={nomeCompleto}
          src={avatarUrl}
          sx={{
            width: dense ? 32 : 56,
            height: dense ? 32 : 56,
            bgcolor: 'background.paper',
          }}
        />
      </Tooltip>
      {!dense && (
        <>
          {/* Menor e em duas linhas, com reticências quando não cabe — o PDV
              corta o nome quando ele é grande demais (`substring(0, 20)` no
              `Profile.js`), aqui o corte é por linha, então nome curto aparece
              inteiro e nome de razão social ("PAROQUIA SAO FRANCISCO DE ASSIS")
              quebra e só depois corta. O tooltip guarda o texto completo. */}
          <Tooltip title={nomeCompleto}>
            <Typography
              variant="subtitle2"
              color="primary"
              align="center"
              sx={{ mt: 1, fontWeight: 600, lineHeight: 1.25, ...duasLinhasSx }}
            >
              {nomeCompleto}
            </Typography>
          </Tooltip>
          <Tooltip title={empresa}>
            <Typography
              variant="caption"
              component="div"
              color="text.secondary"
              align="center"
              sx={{ lineHeight: 1.3, ...duasLinhasSx }}
            >
              {empresa}
            </Typography>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

function containerSx(dense: boolean) {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    py: dense ? 1 : 2,
  } as const;
}
